import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { SystemVersion } from '../api/entities.js';
import { System } from '../api/entities.js';
import { CompanySystem } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { 
  ArrowLeft, 
  Edit, 
  Tag, 
  Package,
  CalendarClock,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ViewSystemVersion() {
  const navigate = useNavigate();
  const [version, setVersion] = useState(null);
  const [system, setSystem] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Obter ID da versão da URL
      const urlParams = new URLSearchParams(window.location.search);
      const versionId = urlParams.get('id');
      
      if (!versionId) {
        navigate(createPageUrl('Systems'));
        return;
      }

      // Verificar permissões do usuário
      const userData = await User.me();
      if (userData.role === 'client') {
        navigate(createPageUrl('Dashboard'));
        return;
      }

      // Carregar versão
      const versionData = await SystemVersion.findById(versionId);
      if (!versionData) {
        setError('Versão não encontrada');
        setIsLoading(false);
        return;
      }
      setVersion(versionData);

      // Carregar sistema relacionado
      const systemData = await System.findById(versionData.system_id);
      if (systemData) {
        setSystem(systemData);
      }

      // Carregar empresas que utilizam esta versão
      const companySystems = await CompanySystem.list({ system_version_id: versionId });
      if (companySystems && companySystems.length > 0) {
        // TODO: Carregar detalhes das empresas
      }
    } catch (err) {
      console.error('Error loading system version:', err);
      setError('Erro ao carregar dados da versão');
    }
    
    setIsLoading(false);
  };

  // Renderizar status da versão como badge
  const renderVersionStatus = (isStable) => {
    return (
      <Badge className={isStable ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
        {isStable ? 'Estável' : 'Em Desenvolvimento'}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" disabled>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-[300px] bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !version || !system) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold">{error || 'Versão não encontrada'}</h2>
        <p className="text-muted-foreground mt-2">
          Não foi possível carregar os dados da versão solicitada.
        </p>
        <Button 
          onClick={() => navigate(createPageUrl('Systems'))}
          className="mt-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Sistemas
        </Button>
      </div>
    );
  }

  // Removido isExpired pois supported_until não existe na estrutura da tabela

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl(`ViewSystem?id=${system.id}`))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{system.name}</h1>
              <span className="text-2xl text-muted-foreground">v{version.version_number}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              {renderVersionStatus(version.is_stable)}
              
              {version.is_latest && (
                <Badge className="bg-indigo-100 text-indigo-800">MAIS RECENTE</Badge>
              )}
              

            </div>
          </div>
        </div>

        <Button 
          onClick={() => navigate(createPageUrl(`EditSystemVersion?id=${version.id}`))}
          className="bg-primary"
        >
          <Edit className="mr-2 h-4 w-4" />
          Editar Versão
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes da Versão</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {version.changelog && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Notas de Lançamento</h3>
                  <div className="p-4 bg-accent rounded-lg whitespace-pre-wrap">
                    {version.changelog}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium mb-2">Datas</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lançamento:</span>
                      <span>
                        {version.release_date 
                          ? format(new Date(version.release_date), 'dd/MM/yyyy', { locale: ptBR })
                          : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Criado em:</span>
                      <span>
                        {version.created_at 
                          ? format(new Date(version.created_at), 'dd/MM/yyyy', { locale: ptBR })
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium mb-2">Informações</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sistema:</span>
                      <span className="font-medium">{system.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      {renderVersionStatus(version.is_stable)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Versão Mais Recente:</span>
                      <span>
                        {version.is_latest 
                          ? <CheckCircle2 className="h-5 w-5 text-green-500" />
                          : <span className="text-gray-500">Não</span>}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Empresas utilizando esta versão */}
          <Card>
            <CardHeader>
              <CardTitle>Empresas Utilizando Esta Versão</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <p className="text-muted-foreground">
                  Nenhuma empresa está utilizando esta versão atualmente.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full justify-start" variant="outline">
                <Tag className="mr-2 h-4 w-4" />
                Marcar como Estável
              </Button>
              
              <Button className="w-full justify-start" variant="outline">
                <AlertTriangle className="mr-2 h-4 w-4" />
                Marcar como Depreciada
              </Button>
              
              <Button 
                className="w-full justify-start text-destructive hover:text-destructive" 
                variant="outline"
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Marcar como Descontinuada
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Outras Versões</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <p className="text-muted-foreground">
                  Carregando outras versões...
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}